import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ActivateIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { agentConfigService, AgentConfiguration, AgentConfigurationCreate, AgentConfigurationUpdate } from '../../services/agentConfigService';

const ConfigurationManager: React.FC = () => {
  const [configurations, setConfigurations] = useState<AgentConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AgentConfiguration | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Form state
  const [formData, setFormData] = useState({
    config_name: '',
    config_type: 'tone' as 'tone' | 'behavior' | 'response_template',
    config_data: '{}',
    is_active: false
  });

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading configurations...');
      const data = await agentConfigService.getConfigurations();
      console.log('✅ Configurations loaded:', data);
      setConfigurations(data);
    } catch (error) {
      console.error('❌ Error loading configurations:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        response: (error as any)?.response?.data,
        status: (error as any)?.response?.status,
        statusText: (error as any)?.response?.statusText
      });
      showSnackbar('Error al cargar configuraciones', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (config?: AgentConfiguration) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        config_name: config.config_name,
        config_type: config.config_type,
        config_data: JSON.stringify(config.config_data, null, 2),
        is_active: config.is_active
      });
    } else {
      setEditingConfig(null);
      setFormData({
        config_name: '',
        config_type: 'tone',
        config_data: '{}',
        is_active: false
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
  };

  const handleSave = async () => {
    try {
      let configData;
      try {
        configData = JSON.parse(formData.config_data);
      } catch (error) {
        showSnackbar('Formato JSON inválido en config_data', 'error');
        return;
      }

      const payload = {
        config_name: formData.config_name,
        config_type: formData.config_type,
        config_data: configData,
        is_active: formData.is_active
      };

      if (editingConfig) {
        await agentConfigService.updateConfiguration(editingConfig.id, payload as AgentConfigurationUpdate);
        showSnackbar('Configuración actualizada exitosamente', 'success');
      } else {
        await agentConfigService.createConfiguration(payload as AgentConfigurationCreate);
        showSnackbar('Configuración creada exitosamente', 'success');
      }

      handleCloseDialog();
      loadConfigurations();
    } catch (error) {
      showSnackbar('Error al guardar configuración', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta configuración?')) {
      try {
        await agentConfigService.deleteConfiguration(id);
        showSnackbar('Configuración eliminada exitosamente', 'success');
        loadConfigurations();
      } catch (error) {
        showSnackbar('Error al eliminar configuración', 'error');
      }
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await agentConfigService.activateConfiguration(id);
      showSnackbar('Configuración activada exitosamente', 'success');
      loadConfigurations();
    } catch (error) {
      showSnackbar('Error al activar configuración', 'error');
    }
  };

  const getConfigTypeColor = (type: string) => {
    switch (type) {
      case 'tone': return 'primary';
      case 'behavior': return 'secondary';
      case 'response_template': return 'info';
      default: return 'default';
    }
  };

  const getConfigTypeLabel = (type: string) => {
    switch (type) {
      case 'tone': return 'Tono';
      case 'behavior': return 'Comportamiento';
      case 'response_template': return 'Plantilla de Respuesta';
      default: return type;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          Gestión de Configuraciones
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Configuración
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Creado</TableCell>
              <TableCell>Actualizado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configurations.map((config) => (
              <TableRow key={config.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {config.config_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getConfigTypeLabel(config.config_type)}
                    color={getConfigTypeColor(config.config_type) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={config.is_active ? 'Activa' : 'Inactiva'}
                    color={config.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(config.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(config.updated_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleActivate(config.id)}
                    disabled={config.is_active}
                    title="Activar configuración"
                  >
                    <ActivateIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(config)}
                    title="Editar configuración"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(config.id)}
                    title="Eliminar configuración"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Configuration Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConfig ? 'Editar Configuración' : 'Nueva Configuración'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nombre de la Configuración"
              value={formData.config_name}
              onChange={(e) => setFormData({ ...formData, config_name: e.target.value })}
              fullWidth
              required
            />
            
            <FormControl fullWidth>
              <InputLabel>Tipo de Configuración</InputLabel>
              <Select
                value={formData.config_type}
                label="Tipo de Configuración"
                onChange={(e) => setFormData({ ...formData, config_type: e.target.value as any })}
              >
                <MenuItem value="tone">Tono</MenuItem>
                <MenuItem value="behavior">Comportamiento</MenuItem>
                <MenuItem value="response_template">Plantilla de Respuesta</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Datos de Configuración (JSON)"
              value={formData.config_data}
              onChange={(e) => setFormData({ ...formData, config_data: e.target.value })}
              multiline
              rows={8}
              fullWidth
              required
              helperText="Ingresa la configuración en formato JSON válido"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Activar esta configuración"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            {editingConfig ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigurationManager;